// src/components/landing/ScoutDeskSection.jsx
import "./ScoutDeskSection.css";

const FEATURES = [
  {
    id: "flashcards",
    label: "Flashcards",
    category: "Practice",
    headline: "Flashcards that adapt to your course",
    proof: "Built from your uploaded docs",
    body: "Generate study-ready flashcards directly from your uploaded materials — not generic decks.",
    bullets: [
      "Auto-generated from PDFs, slides, notes",
      "Clean review flow built for repetition",
      "Stay focused on what you don't know"
    ],
    screenshot: "/landing_page_hero/features_section/flaschard_tool_screenshot.png",
    scoutAccent: "/landing_page_hero/features_section/scout_clipboard.png",
    scoutPosition: "bottom-left"
  },
  {
    id: "quizzes",
    label: "Quizzes",
    category: "Practice",
    headline: "Exam-ready practice in minutes",
    proof: "Designed for active recall",
    body: "Turn course content into multiple-choice questions that feel like real test prep.",
    bullets: [
      "Structured question navigation",
      "Designed for active recall",
      "Fast iteration as your materials change"
    ],
    screenshot: "/landing_page_hero/features_section/quiz_tool_screenshot.png",
    scoutAccent: "/landing_page_hero/features_section/Scout_Pointer.png",
    scoutPosition: "top-right"
  },
  {
    id: "podcasts",
    label: "Podcasts",
    category: "Listen",
    headline: "Turn readings into listening time",
    proof: "Study anywhere, anytime",
    body: "Convert documents into a podcast-style explanation you can use anywhere.",
    bullets: [
      "Great for commuting / workouts",
      "Choose depth: brief → exhaustive",
      "Built around your course docs",
      "For people who learn best when listening to a conversation"
    ],
    screenshot: "/landing_page_hero/features_section/podcasts_tool_screenshot.png",
    scoutAccent: "/landing_page_hero/features_section/scout_thinking.png",
    scoutPosition: "bottom-right"
  },
  {
    id: "audiobooks",
    label: "Audiobooks",
    category: "Listen",
    headline: "When you don't have time to read",
    proof: "Dense content, digestible format",
    body: "Transform uploaded documents into audiobook-style listening with clear structure.",
    bullets: [
      "Choose length and detail level",
      "Keeps you moving without losing content",
      "Perfect for dense reading weeks"
    ],
    screenshot: "/landing_page_hero/features_section/audiobook_tool_screenshot.png",
    scoutAccent: "/landing_page_hero/features_section/scout_thinking.png",
    scoutPosition: "top-left"
  },
  {
    id: "course-chat",
    label: "Course Chat",
    category: "Understand",
    headline: "Course-specific answers, grounded in your docs",
    proof: "Course-context aware",
    body: "Ask questions and get clear explanations using the full course context.",
    bullets: [
      "Uses your processed course documents",
      "Great for clarification + review",
      "Keeps your studying organized by class"
    ],
    screenshot: "/landing_page_hero/features_section/chat_tool_screenshot.png",
    scoutAccent: "/landing_page_hero/features_section/scout_clipboard.png",
    scoutPosition: "bottom-right"
  }
];

export default function ScoutDeskSection() {
  return (
    <section
      id="features"
      className="scoutDeskSection"
      aria-label="AI Study Tools"
    >
      <div className="featuresContainer">
        {/* Section Header */}
        <div className="featuresHeader">
          <h2 className="featuresTitle">Study tools built for real college life</h2>
          <p className="featuresSubtitle">
            When you're working, commuting, or catching up late, DocuHounds helps you keep moving.
          </p>
          <p className="featuresHelper">Built from your actual coursework.</p>
          <div className="featuresSuiteStat">
            5 tools \u00b7 1 workspace \u00b7 built for real coursework
          </div>
        </div>

        {/* Feature Blocks */}
        <div className="featuresBlocks">
          {FEATURES.map((feature, index) => {
            const isReversed = index % 2 === 1;
            
            return (
              <div 
                key={feature.id} 
                className={`featureBlock ${isReversed ? 'featureBlock--reversed' : ''}`}
                data-category={feature.category}
              >
                {/* Copy Column */}
                <div className="featureCopy">
                  <h3 className="featureHeadline">{feature.headline}</h3>
                  <p className="featureProof">{feature.proof}</p>
                  <p className="featureBody">{feature.body}</p>
                  <ul className="featureBullets">
                    {feature.bullets.map((bullet, bulletIndex) => (
                      <li key={bulletIndex}>{bullet}</li>
                    ))}
                  </ul>
                </div>

                {/* Screenshot Column */}
                <div className="featureScreenshot">
                  <div className="screenshotLabel">{feature.label}</div>
                  <div className="screenshotFrame">
                    <img 
                      src={feature.screenshot} 
                      alt={`${feature.label} screenshot`}
                      className="screenshotImage"
                    />
                    <img 
                      src={feature.scoutAccent} 
                      alt=""
                      className={`scoutAccent scoutAccent--${feature.scoutPosition}`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
